import React, { useEffect, useRef, useState } from 'react';
import styles from './styles/UserCreditCards.module.scss';
import { IAppState, ICreditCard } from 'types';
import { getCCref } from 'helpers';
import { CreditCardBenefitsImg } from 'constants/explore-cc';
import { Plus, X } from 'react-feather';
import ExploreCreditCards from 'components/explore-cc';
import { useDispatch, useSelector } from 'react-redux';
import { selectCreditCardsRef } from 'selectors/referenceSelectors';
import {
  DeleteCreditCardPolicy,
  CreateCreditCardPolicy,
  CreditCardResetForm,
} from 'actions';
import { BackToTop } from 'components/quashed';

const CardBenefits = ({ benefits }) => {
  return (
    <div>
      {benefits.map(({ name, description }) => {
        const SvgComponent =
          CreditCardBenefitsImg[name] || CreditCardBenefitsImg.default;
        return (
          <div className={styles.benefits}>
            <div className={styles.benefitHeader}>
              <SvgComponent key={name} className={styles.benefitIcon} />
              <div>{name}</div>
            </div>
            <div className={styles.benefitDescription}>{description}</div>
          </div>
        );
      })}
    </div>
  );
};

const CreditCardContainer = ({ card, refCreditCards, deleteCreditCard }) => {
  const cardInfo = getCCref(card, refCreditCards);

  const handleDeleteCreditCard = () => {
    deleteCreditCard(card.id, card.sk);
  };

  return (
    <div className={styles.cardContainer}>
      <div className={styles.cardImageContainer}>
        <img
          alt={cardInfo.name}
          className={styles.cardImage}
          src={`https:${cardInfo.cardImageUrl}`}
        ></img>
        <div className={styles.closeIcon}>
          <X size={14} onClick={handleDeleteCreditCard} />
        </div>
      </div>

      <div className={styles.cardName}>{cardInfo.name}</div>
      <CardBenefits benefits={cardInfo.benefits} />
    </div>
  );
};

const UserCreditCards = () => {
  const dispatch = useDispatch();
  const ref = useRef(null);
  const [addCC, setAddCC] = useState(false);
  const refCreditCards = useSelector(selectCreditCardsRef);
  const {
    creditCard: { creditCards, submitted, submitting, loaded },
    user,
  } = useSelector<IAppState, IAppState>(R.pick(['creditCard', 'user']));

  const deleteCreditCard = (id: string, sk: string) =>
    dispatch(DeleteCreditCardPolicy(id, sk));
  const createCreditCardPolicy = (cc: ICreditCard) =>
    dispatch(CreateCreditCardPolicy(cc));
  const resetCreditCardForm = () => dispatch(CreditCardResetForm());

  const handleScrollToTop = () => {
    ref.current.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAddCard = () => {
    setAddCC((value) => !value);
  };

  const handleBack = () => {
    setAddCC(false);
  };

  const hasNoCc = loaded && creditCards.length === 0;
  const showBack = !submitted && !hasNoCc;

  useEffect(() => {
    // Start add CC flow if there
    // are no CC after its loaded
    if (hasNoCc) {
      setAddCC(true);
    }
  }, [hasNoCc]);

  return (
    <div className={styles.container} ref={ref}>
      <div className={styles.header}>
        {addCC ? (
          showBack && (
            <div className={styles.addBtn} onClick={handleAddCard}>
              Back
            </div>
          )
        ) : (
          <>
            <div className={styles.title}>My card benefits</div>
            <div className={styles.addBtn} onClick={handleAddCard}>
              <Plus size={16} />
              Add
            </div>
          </>
        )}
      </div>
      {addCC ? (
        <ExploreCreditCards
          submitted={submitted}
          submitting={submitting}
          creditCards={refCreditCards}
          user={user}
          createCreditCardPolicy={createCreditCardPolicy}
          resetForm={resetCreditCardForm}
          handleBack={handleBack}
        />
      ) : (
        <>
          <div className={styles.allCardsContainer}>
            {creditCards.map((card) => {
              return (
                <CreditCardContainer
                  card={card}
                  refCreditCards={refCreditCards}
                  deleteCreditCard={deleteCreditCard}
                />
              );
            })}
          </div>
          <BackToTop onClick={handleScrollToTop} />
        </>
      )}
    </div>
  );
};

export default UserCreditCards;
